import { Box, Container, Divider, Link } from "@chakra-ui/react"
import Hero from "@components/blocks/Hero"
import HeadingText from "@components/headings/HeadingText"
import NormalHeading from "@components/headings/NormalHeading"
import StylizedHeading from "@components/headings/StylizedHeading"
import HeaderLayout from "@components/layout/HeaderLayout"
import { EmenuPagesTranslation } from "@definitions/types"
import { readItem, readItems } from "@directus/sdk"
import { routing } from "@i18n/routing"
import ImageOne from "@public/images/hero/why-image-1.jpg"
import ImageTwo from "@public/images/hero/why-image-2.jpg"
import ImageThree from "@public/images/hero/why-image-3.jpg"
import ImageFour from "@public/images/hero/why-image-4.jpg"
import ImageFive from "@public/images/hero/why-image-5.png"
import SvgWhy3 from "@public/svg/features/contactless.svg"
import SvgWhy2 from "@public/svg/features/delivery-zones.svg"
import SvgWhy1 from "@public/svg/features/promos.svg"
import directusClient from "@utils/directus"
import { loadMessages } from "@utils/utils"
import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next"
import dynamic from "next/dynamic"
import NextLink from "next/link"
import { useTranslations } from "next-intl"
import { NextSeo } from "next-seo"

const DynamicSales = dynamic(() => import("@components/blocks/why/Sales"), {
  ssr: false,
})
const DynamicHighlight = dynamic(
  () => import("@components/blocks/why/Highlight"),
  { ssr: false }
)
const DynamicDesign = dynamic(() => import("@components/blocks/why/Design"), {
  ssr: false,
})
const DynamicJourney = dynamic(() => import("@components/blocks/why/Journey"), {
  ssr: false,
})
const DynamicClientGrid = dynamic(
  () => import("@components/blocks/ClientGrid"),
  { ssr: false }
)
const DynamicFooter = dynamic(() => import("@components/layout/Footer"), {
  ssr: false,
})

const DynamicDashboard = dynamic(() => import("@components/blocks/Dashboard"), {
  ssr: false,
})

const WhyEmenu: NextPage = ({
  clients,
  page_data,
  locales,
  locale,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const t = useTranslations("why")

  return (
    <>
      <NextSeo
        title={page_data.meta_title}
        description={page_data.meta_description}
        canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/${page_data.slug}`}
        languageAlternates={locales.map((lang: string) => {
          const prefix = lang === routing.defaultLocale ? "" : `/${lang}`
          return {
            hrefLang: lang,
            href: `${process.env.NEXT_PUBLIC_SITE_URL}${prefix}/${page_data.slug}`,
          }
        })}
        additionalMetaTags={[
          {
            name: "keywords",
            content: page_data.meta_keywords,
          },
        ]}
      />
      <HeaderLayout>
        <Hero
          iconOne={SvgWhy1}
          iconTwo={SvgWhy2}
          iconThree={SvgWhy3}
          imageOne={ImageOne}
          imageTwo={ImageTwo}
          imageThree={ImageThree}
          imageFour={ImageFour}
          imageFive={ImageFive}
          alt={t("why_emenu")}
        />
      </HeaderLayout>
      <Container
        maxW={"container.xl"}
        px={{ base: 10, xl: 0 }}
      >
        <NormalHeading as={"h1"}>
          {t("designed_to_increase_sales")}
        </NormalHeading>
        <StylizedHeading>{t("by_30")}</StylizedHeading>
        <HeadingText>
          {t("designed_to_increase_sales_text")}
          <Link
            as={NextLink}
            href={"/blog/why-should-restaurants-switch-to-a-smart-digital-menu"}
            borderBottomWidth={2}
            borderStyle={"solid"}
            borderColor={"brand.secondary"}
            _hover={{ textDecoration: "none", borderColor: "brand.primary" }}
          >
            {t("check_our_article")}
          </Link>
          {"."}
        </HeadingText>
        <DynamicSales />

        <NormalHeading>{t("smart_dashboards")}</NormalHeading>
        <StylizedHeading>{t("customers")}</StylizedHeading>
        <DynamicDashboard />

        <NormalHeading>{t("new_emenu_setup")}</NormalHeading>
        <StylizedHeading>{t("unique_design")}</StylizedHeading>
        <HeadingText>{t("unique_design_text")}</HeadingText>
        <DynamicDesign />

        <DynamicHighlight />

        <NormalHeading
          color={"brand.black"}
          textTransform={"uppercase"}
        >
          {t("dedicated")}{" "}
          <Box
            as={"span"}
            fontFamily={locale === "ar" ? "Arslan Wessam" : "Wild Spirit Alt"}
            fontWeight={400}
            fontSize={{ base: "4xl", md: "5xl", lg: "7xl" }}
            color={"brand.primary"}
            textTransform={"capitalize"}
            p={2}
          >
            {t("customer")}
          </Box>{" "}
          {t("support")}
        </NormalHeading>

        <Divider
          mt={100}
          borderBottomWidth={2}
          borderColor={"brand.secondary"}
        />

        <NormalHeading>{t("your_valuable_customer")}</NormalHeading>
        <StylizedHeading>{t("journey")}</StylizedHeading>
        <DynamicJourney />

        <Divider
          mt={100}
          borderBottomWidth={2}
          borderColor={"brand.secondary"}
        />

        <NormalHeading
          color={"brand.secondary"}
          textTransform={"uppercase"}
          mt={100}
          mb={{ base: 10, md: 100 }}
        >
          {t("trusted_by")}
        </NormalHeading>
        <DynamicClientGrid clients={clients} />
      </Container>
      <DynamicFooter />
    </>
  )
}

export default WhyEmenu

export const getStaticProps: GetStaticProps = async ({ locale, locales }) => {
  // Clients data
  const clients = await directusClient.request(
    readItems("foodphotography_clients", {
      limit: 40,
      fields: [
        "id",
        "name",
        "image.filename_disk",
        "image.width",
        "image.height",
      ],
      filter: {
        status: {
          _eq: "published",
        },
      },
    })
  )

  // Page meta data
  const page_data = await directusClient
    .request(
      readItem("emenu_pages", 10, {
        fields: ["slug", { translations: ["*"] }],
      })
    )
    .then((res) => {
      const resultTranslations = res?.translations as EmenuPagesTranslation[]
      const translations = resultTranslations?.find(
        (t) => t.languages_id === locale
      )
      return {
        slug: res?.slug,
        meta_title: translations?.meta_title,
        meta_description: translations?.meta_description,
        meta_keywords: translations?.meta_keywords,
        title: translations?.title,
        content: translations?.content,
      }
    })

  const [shared, why] = await Promise.all([
    loadMessages("shared", locale ?? routing.defaultLocale),
    loadMessages("why", locale ?? routing.defaultLocale),
  ])

  return {
    props: {
      clients,
      page_data,
      locales,
      locale,
      messages: { ...shared, ...why },
    },
  }
}
